import type { Component } from 'vue'
import { OnboardingDraftData } from '../shared/api/generated/models/onboardingDraftData.ts'

/**
 * The step registry is the extensibility seam of the wizard.
 *
 * Adding a future setup section ("Shift patterns", "Pay rates", ...) means:
 *   1. write a step component under steps/<name>/
 *   2. add one entry to the `steps` array below
 * The layout, rail, routing, progress and review step all derive from this
 * array and need no changes.
 */
export interface StepDefinition {
  id: string
  title: string
  /** Short line shown under the title in the step header. */
  description: string
  /** Name of an icon in your icon set; rendered by StepRail. */
  icon: string
  /** Lazy-loaded so each step is its own chunk. */
  component: () => Promise<{ default: Component }>
  /** Can the user press "Skip for now"? */
  isSkippable: boolean
  /** Drives the tick in the rail and the review summary. */
  isComplete: (draft: OnboardingDraftData) => boolean
  /**
   * Hide steps that make no sense yet (e.g. Areas with zero buildings).
   * Omitted = always enabled.
   */
  isEnabled?: (draft: OnboardingDraftData) => boolean
}

export const steps: StepDefinition[] = [
  {
    id: 'sector',
    title: 'Business profile',
    description: 'Tell us what kind of business you run so we can tailor suggestions.',
    icon: 'briefcase',
    component: () => import('./components/steps/SectorStep.vue'),
    isSkippable: false,
    isComplete: (d) => d.sector !== null,
  },
  {
    id: 'buildings',
    title: 'Buildings',
    description: 'Buildings are physical locations or a grouping of areas.',
    icon: 'building',
    component: () => import('./components/steps/BuildingsStep.vue'),
    isSkippable: true,
    isComplete: (d) => d.buildings.length > 0,
  },
  {
    id: 'areas',
    title: 'Areas',
    description: 'Areas are where shifts occur.',
    icon: 'map-pin',
    component: () => import('./components/steps/AreasStep.vue'),
    isSkippable: true,
    isComplete: (d) =>
      d.buildings.length > 0 &&
      d.buildings.every((b) => d.areas.some((a) => a.buildingTempId === b.tempId)),
    isEnabled: (d) => d.buildings.length > 0,
  },
  {
    id: 'skills',
    title: 'Skills',
    description: 'Requirements a worker must have to be eligible for a shift.',
    icon: 'award',
    component: () => import('./components/steps/SkillsStep.vue'),
    isSkippable: true,
    isComplete: (d) => d.skills.length > 0,
  },
//   {
//     id: 'team',
//     title: 'Team',
//     description: 'Invite the people you schedule. You can always add more later.',
//     icon: 'users',
//     component: () => import('./team/TeamStep.vue'),
//     isSkippable: true,
//     isComplete: (d) => d.employees.length > 0,
//   },
  {
    id: 'review',
    title: 'Summary',
    description: 'Check information before submitting.',
    icon: 'checklist',
    component: () => import('./components/steps/ReviewStep.vue'),
    isSkippable: false,
    isComplete: () => false, // completing the review step exits the wizard
  },
]

export function enabledSteps(draft: OnboardingDraftData): StepDefinition[] {
  return steps.filter((s) => s.isEnabled?.(draft) ?? true)
}

export function stepById(id: string): StepDefinition | undefined {
  return steps.find((s) => s.id === id)
}

/** First enabled step that is neither complete nor explicitly skipped. */
export function firstIncompleteStep(draft: OnboardingDraftData): StepDefinition {
  const list = enabledSteps(draft)
  return (
    list.find((s) => !s.isComplete(draft) && !draft.skippedStepIds.includes(s.id)) ??
    list[list.length - 1]
  )
}
